/**
 * Категории — витрина, а не учёт: в FOM их нет. Порядок показа, кэшбэк,
 * названия на двух языках и состав задаются здесь (13-business-decisions.md).
 */
import { arrayMove } from '@dnd-kit/sortable'
import { withCountRu } from '@dorixona/utils'
import type { Category } from './api'

export const sortedCategories = (categories: Category[]): Category[] =>
  [...categories].sort((a, b) => a.sort - b.sort)

/** Поиск идёт и по узбекскому названию: его как раз и проверяют. */
export function filterCategories(categories: Category[], query: string): Category[] {
  const q = query.toLowerCase().trim()
  if (!q) return categories
  return categories.filter(
    (c) => c.name.toLowerCase().includes(q) || c.nameUz.toLowerCase().includes(q),
  )
}

/** Кэшбэк категории: целые проценты от 0 до 30. */
export const clampCashback = (value: number): number =>
  Math.max(0, Math.min(30, Math.round(value || 0)))

/**
 * Перенос категории перетаскиванием на место другой. Значения порядка не
 * придумываем заново, а раздаём прежние по новому списку — так меняются только
 * категории между старым и новым местом, и сохранять приходится их одних.
 * Если значения повторяются (две категории с одним `sort`), раздаём 1, 2, 3…:
 * иначе перенос между «равными» ничего бы не изменил.
 */
export function reorderCategories(
  categories: Category[],
  activeId: number,
  overId: number,
): Category[] {
  const all = sortedCategories(categories)
  const from = all.findIndex((c) => c.id === activeId)
  const to = all.findIndex((c) => c.id === overId)
  if (from < 0 || to < 0) return all
  const sorts = all.map((c) => c.sort)
  const distinct = new Set(sorts).size === sorts.length
  return arrayMove(all, from, to).map((c, i) => ({
    ...c,
    sort: distinct ? (sorts[i] as number) : i + 1,
  }))
}

/** Категории, у которых после переноса поменялся порядок, — их и сохраняем. */
export function changedOrder(before: Category[], after: Category[]): Category[] {
  return after.filter((c) => before.find((b) => b.id === c.id)?.sort !== c.sort)
}

/** То, что спрашивает окно «Новая категория»: остальное задают в карточке. */
/**
 * Новая категория — одно русское название: остальное (узбекское название,
 * описания, кэшбэк, обложку, видимость) заполняют в карточке, куда окно и
 * ведёт после создания. Встаёт в конец витрины и пока скрыта: пустая плитка
 * без обложки и товаров в приложении ни к чему.
 */
export function newCategory(name: string, categories: Category[]): Category {
  return {
    id: 0,
    name: name.trim(),
    nameUz: '',
    desc: '',
    descUz: '',
    nameEn: '',
    descEn: '',
    cb: 0,
    sort: categories.reduce((max, c) => Math.max(max, c.sort), 0) + 1,
    active: false,
    products: 0,
    cover: '',
    coverUrl: '',
  }
}

/**
 * Почему категорию не удалили: сервер отвечает 409 `in_use`, пока в ней есть
 * товары, — скрытые тоже. Сколько их, говорим, если уже знаем.
 */
export function inUseText(count: number | undefined): string {
  return count
    ? `Сначала перенесите товары в другую категорию — в этой ещё ${withCountRu(count, ['товар', 'товара', 'товаров'])}`
    : 'Сначала перенесите товары в другую категорию — удалить можно только пустую'
}
